import { v4 as uuidv4 } from "uuid";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDynamicSecurityQuestionsOptions } from "hooks/useDynamicSecurityQuestionsOptions";
import { Trans, useTranslation } from "react-i18next";
import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";

import FormInputError from "components/General/FormInputError";
import PaperlessSettingDialog from "components/ModalDialogs/PaperlessSettingDialog";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";

const PanelThree = ({
  customerRegistrationFormData,
  setCustomerRegistrationFormData,
  setCurrentStep,
  setConfirmationPassCode,
  setLoading,
}) => {
  const uuid = uuidv4();
  const { t } = useTranslation();
  const {
    setSecurityQuestionOneId,
    securityQuestionsOne,

    setSecurityQuestionTwoId,
    securityQuestionsTwo,

    setSecurityQuestionThreeId,
    securityQuestionsThree,
  } = useDynamicSecurityQuestionsOptions();

  const [paperlessDialogState, setPaperlessDialogState] = useState(false);
  const [paperlessConfirmation, setPaperlessConfirmation] = useState(false);
  const [questionsOne, setQuestionsOne] = useState({
    value: customerRegistrationFormData?.securityQuestionOne ?? "all",
  });
  const [questionsTwo, setQuestionsTwo] = useState({
    value: customerRegistrationFormData?.securityQuestionTwo ?? "all",
  });
  const [questionsThree, setQuestionsThree] = useState({
    value: customerRegistrationFormData?.securityQuestionThree ?? "all",
  });

  const {
    register: registrationPanelThreeRegister,
    getValues,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    setError,
    formState: { errors: registrationPanelThreeErrors },
  } = useForm({
    defaultValues: customerRegistrationFormData,
  });

  useEffect(() => {
    if (paperlessConfirmation) setCurrentStep(4);
  }, [paperlessConfirmation]);

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  async function onSubmit(data) {
    if (data?.paperless === "true") {
      setPaperlessDialogState(true);
    }
    let passcode = randomNumberInRange(100000, 999999);

    setConfirmationPassCode(passcode.toString());
    setLoading(true);
    let securitycode = randomNumberInRange(100000, 999999);
    let sec =
      parseInt(securitycode.toString().substring(3, 4)) === 0
        ? 1
        : parseInt(securitycode.toString().substring(3, 4));
    let confirmationcode = passcode * sec;

    let contentDictionary = {};
    contentDictionary.confirmationcode = confirmationcode.toString();
    contentDictionary.username =
      data.policyOwnerType === "individual"
        ? data.firstname
        : data.businessName;
    contentDictionary.securitycode = securitycode.toString();

    await sendEmailNotification(data.email, 2, contentDictionary)
      .then((response) => {
        setLoading(false);
        setCurrentStep(4);
        setCustomerRegistrationFormData((curr) => ({
          ...curr,
          ...data,
        }));
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  }
  return (
    <>
      <div className="mt-2 border-t border-theme-rule-medium">
        <div>
          <form
            className="pt-2 border-theme-rule-medium"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="mt-4">
              <Controller
                name="securityQuestionOne"
                control={control}
                render={() => (
                  <RELEASING_AutoCompleteComboBox
                    labelName={t("CustomerRegister-step-3-form-label-2")}
                    inputPlaceholderText={t(
                      "CustomerRegister-step-3-form-label-3"
                    )}
                    options={[
                      {
                        value: "all",
                        displayAs: t("CustomerRegister-step-3-form-label-3"),
                        selectedPolicyDisplayAs: t(
                          "CustomerRegister-step-3-form-label-3"
                        ),
                        isDisplayOnly: true,
                      },
                      ...securityQuestionsOne.map((question) => ({
                        value: question.id,
                        displayAs: question.question,
                        selectedPolicyDisplayAs: question.question,
                        isDisplayOnly: true,
                      })),
                    ]}
                    useSmallFont={true}
                    selectedOption={questionsOne}
                    setSelectedOption={(option) => {
                      if (option?.value !== "all") {
                        clearErrors("securityQuestionOne");
                        setValue("securityQuestionOne", option?.value);
                        setSecurityQuestionOneId(option?.value);
                      } else {
                        setError("securityQuestionOne", {
                          message: t("CustomerRegister-step-3-form-error-1"),
                        });
                        setValue("securityQuestionOne", "");
                      }
                      setQuestionsOne(option?.value);
                    }}
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: t("CustomerRegister-step-3-form-error-1"),
                  },
                }}
              />
              {registrationPanelThreeErrors.securityQuestionOne && (
                <FormInputError
                  id={`${uuid}-question1Error`}
                  errorMessage={
                    registrationPanelThreeErrors.securityQuestionOne.message
                  }
                />
              )}
            </div>

            <div className="mt-4">
              <label id={`${uuid}-answer1Label`}>
                {t("CustomerRegister-step-3-form-label-4")}
              </label>
              <input
                id={`${uuid}-answer1`}
                className="w-full mt-1"
                type="text"
                {...registrationPanelThreeRegister("securtityAnswerOne", {
                  required: {
                    value: true,
                    message: t("CustomerRegister-step-3-form-error-2"),
                  },
                  validate: {
                    answerRuleBySelectedQuestion: (v) => {
                      switch (getValues("securityQuestionOne")) {
                        case "6":
                          return (
                            (/^\d{4}$/.test(v) &&
                              parseInt(v) <= new Date().getFullYear()) ||
                            t("CustomerRegister-step-3-form-error-3")
                          );
                        case "8":
                          if (
                            v.split("/").length !== 2 ||
                            v.split("/")[0].length !== 2 ||
                            v.split("/")[1].length !== 2
                          )
                            return t("CustomerRegister-step-3-form-error-4");
                          if (
                            parseInt(v.split("/")[0]) < 1 ||
                            parseInt(v.split("/")[0]) > 12 ||
                            parseInt(v.split("/")[1]) < 1 ||
                            parseInt(v.split("/")[1]) > 31
                          )
                            return t("CustomerRegister-step-3-form-error-5");
                          break;
                        case "14":
                          return (
                            /^\d{10}$/.test(v) ||
                            t("CustomerRegister-step-3-form-error-6")
                          );
                        default:
                          return (
                            /^[A-Za-z ]+$/.test(v) ||
                            t("CustomerRegister-step-3-form-error-7")
                          );
                      }
                    },
                  },
                  maxLength: {
                    value: 100,
                    message: t("CustomerRegister-step-3-form-error-8"),
                  },
                })}
                aria-labelledby={`${uuid}-answer1Label`}
              />

              {registrationPanelThreeErrors.securtityAnswerOne && (
                <FormInputError
                  id={`${uuid}-question1Error`}
                  errorMessage={
                    registrationPanelThreeErrors.securtityAnswerOne.message
                  }
                />
              )}
            </div>

            <div className="mt-4">
              <Controller
                name="securityQuestionTwo"
                control={control}
                render={() => (
                  <RELEASING_AutoCompleteComboBox
                    labelName={t("CustomerRegister-step-3-form-label-5")}
                    inputPlaceholderText={t(
                      "CustomerRegister-step-3-form-label-3"
                    )}
                    options={[
                      {
                        value: "all",
                        displayAs: t("CustomerRegister-step-3-form-label-3"),
                        selectedPolicyDisplayAs: t(
                          "CustomerRegister-step-3-form-label-3"
                        ),
                        isDisplayOnly: true,
                      },
                      ...securityQuestionsTwo.map((question) => ({
                        value: question.id,
                        displayAs: question.question,
                        selectedPolicyDisplayAs: question.question,
                        isDisplayOnly: true,
                      })),
                    ]}
                    useSmallFont={true}
                    selectedOption={questionsTwo}
                    setSelectedOption={(option) => {
                      if (option?.value !== "all") {
                        clearErrors("securityQuestionTwo");
                        setValue("securityQuestionTwo", option?.value);
                        setSecurityQuestionTwoId(option?.value);
                      } else {
                        setError("securityQuestionTwo", {
                          message: t("CustomerRegister-step-3-form-error-1"),
                        });
                        setValue("securityQuestionTwo", "");
                      }
                      setQuestionsTwo(option?.value);
                    }}
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: t("CustomerRegister-step-3-form-error-1"),
                  },
                }}
              />

              {registrationPanelThreeErrors.securityQuestionTwo && (
                <FormInputError
                  id={`${uuid}-question2Error`}
                  errorMessage={
                    registrationPanelThreeErrors.securityQuestionTwo.message
                  }
                />
              )}
            </div>

            <div className="mt-4">
              <label id={`${uuid}-answer2Label`}>
                {t("CustomerRegister-step-3-form-label-6")}
              </label>
              <input
                id={`${uuid}-answer2`}
                className="w-full mt-1"
                type="text"
                {...registrationPanelThreeRegister("securtityAnswerTwo", {
                  required: {
                    value: true,
                    message: t("CustomerRegister-step-3-form-error-2"),
                  },
                  validate: {
                    answerRuleBySelectedQuestion: (v) => {
                      switch (getValues("securityQuestionTwo")) {
                        case "6":
                          return (
                            (/^\d{4}$/.test(v) &&
                              parseInt(v) <= new Date().getFullYear()) ||
                            t("CustomerRegister-step-3-form-error-3")
                          );
                        case "8":
                          if (
                            v.split("/").length !== 2 ||
                            v.split("/")[0].length !== 2 ||
                            v.split("/")[1].length !== 2
                          )
                            return t("CustomerRegister-step-3-form-error-4");
                          if (
                            parseInt(v.split("/")[0]) < 1 ||
                            parseInt(v.split("/")[0]) > 12 ||
                            parseInt(v.split("/")[1]) < 1 ||
                            parseInt(v.split("/")[1]) > 31
                          )
                            return t("CustomerRegister-step-3-form-error-5");
                          break;
                        case "14":
                          return (
                            /^\d{10}$/.test(v) ||
                            t("CustomerRegister-step-3-form-error-6")
                          );
                        default:
                          return (
                            /^[A-Za-z ]+$/.test(v) ||
                            t("CustomerRegister-step-3-form-error-7")
                          );
                      }
                    },
                  },
                  maxLength: {
                    value: 100,
                    message: t("CustomerRegister-step-3-form-error-8"),
                  },
                })}
                aria-labelledby={`${uuid}-answer2Label`}
              />

              {registrationPanelThreeErrors.securtityAnswerTwo && (
                <FormInputError
                  id={`${uuid}-question2Error`}
                  errorMessage={
                    registrationPanelThreeErrors.securtityAnswerTwo.message
                  }
                />
              )}
            </div>

            <div className="mt-4">
              <Controller
                name="securityQuestionThree"
                control={control}
                render={() => (
                  <RELEASING_AutoCompleteComboBox
                    labelName={t("CustomerRegister-step-3-form-label-8")}
                    inputPlaceholderText={t(
                      "CustomerRegister-step-3-form-label-3"
                    )}
                    options={[
                      {
                        value: "all",
                        displayAs: t("CustomerRegister-step-3-form-label-3"),
                        selectedPolicyDisplayAs: t(
                          "CustomerRegister-step-3-form-label-3"
                        ),
                        isDisplayOnly: true,
                      },
                      ...securityQuestionsThree.map((question) => ({
                        value: question.id,
                        displayAs: question.question,
                        selectedPolicyDisplayAs: question.question,
                        isDisplayOnly: true,
                      })),
                    ]}
                    useSmallFont={true}
                    selectedOption={questionsThree}
                    setSelectedOption={(option) => {
                      if (option?.value !== "all") {
                        clearErrors("securityQuestionThree");
                        setValue("securityQuestionThree", option?.value);
                        setSecurityQuestionThreeId(option?.value);
                      } else {
                        setError("securityQuestionThree", {
                          message: t("CustomerRegister-step-3-form-error-1"),
                        });
                        setValue("securityQuestionThree", "");
                      }
                      setQuestionsThree(option?.value);
                    }}
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: t("CustomerRegister-step-3-form-error-1"),
                  },
                }}
              />

              {registrationPanelThreeErrors.securityQuestionThree && (
                <FormInputError
                  id={`${uuid}-question3Error`}
                  errorMessage={
                    registrationPanelThreeErrors.securityQuestionThree.message
                  }
                />
              )}
            </div>

            <div className="mt-4">
              <label id={`${uuid}-answer3Label`}>
                {t("CustomerRegister-step-3-form-label-8")}
              </label>
              <input
                id={`${uuid}-answer3`}
                className="w-full mt-1"
                type="text"
                {...registrationPanelThreeRegister("securtityAnswerThree", {
                  required: {
                    value: true,
                    message: t("CustomerRegister-step-3-form-error-2"),
                  },
                  validate: {
                    answerRuleBySelectedQuestion: (v) => {
                      switch (getValues("securityQuestionThree")) {
                        case "6":
                          return (
                            (/^\d{4}$/.test(v) &&
                              parseInt(v) <= new Date().getFullYear()) ||
                            t("CustomerRegister-step-3-form-error-3")
                          );
                        case "8":
                          if (
                            v.split("/").length !== 2 ||
                            v.split("/")[0].length !== 2 ||
                            v.split("/")[1].length !== 2
                          )
                            return t("CustomerRegister-step-3-form-error-4");
                          if (
                            parseInt(v.split("/")[0]) < 1 ||
                            parseInt(v.split("/")[0]) > 12 ||
                            parseInt(v.split("/")[1]) < 1 ||
                            parseInt(v.split("/")[1]) > 31
                          )
                            return t("CustomerRegister-step-3-form-error-5");
                          break;
                        case "14":
                          return (
                            /^\d{10}$/.test(v) ||
                            t("CustomerRegister-step-3-form-error-6")
                          );
                        default:
                          return (
                            /^[A-Za-z ]+$/.test(v) ||
                            t("CustomerRegister-step-3-form-error-7")
                          );
                      }
                    },
                  },
                  maxLength: {
                    value: 100,
                    message: t("CustomerRegister-step-3-form-error-8"),
                  },
                })}
                aria-labelledby={`${uuid}-answer3Label`}
              />
              {registrationPanelThreeErrors.securtityAnswerThree && (
                <FormInputError
                  id={`${uuid}-answer3Error`}
                  errorMessage={
                    registrationPanelThreeErrors.securtityAnswerThree.message
                  }
                />
              )}
            </div>
            <div className="mt-4 text-right">
              <button
                type="button"
                className="ui-btn-secondary ui-btn-min-w mt-2"
                onClick={() => setCurrentStep(2)}
              >
                <Trans
                  i18nKey="CustomerRegister-step-2-form-button-1"
                  components={{
                    srText: <span className="sr-only"></span>,
                  }}
                ></Trans>
              </button>
              <button
                type="submit"
                className="ui-btn-primary ui-btn-min-w mt-2"
              >
                <Trans
                  i18nKey="CustomerRegister-step-3-form-label-9"
                  components={{
                    srText: <span className="sr-only"></span>,
                  }}
                ></Trans>
              </button>
            </div>
            <div>
              <label hidden={true}></label>
            </div>
          </form>
        </div>
      </div>

      <PaperlessSettingDialog
        state={paperlessDialogState}
        setState={setPaperlessDialogState}
        setPaperlessConfirmation={setPaperlessConfirmation}
      />
    </>
  );
};

export default PanelThree;
